/**
 * COPY STRATEGY — TaxInstallmentPage
 *
 * Awareness:      Most-Aware (Stage 5/5)
 *                 Reader already has tax debt and is looking for an immediate
 *                 solution. Knows the problem, knows they owe money, needs action.
 *
 * Sophistication: Stage 1 — Direct Claim
 *                 "Mokėti dalimis per VMI per 5 minutes" — direct solution.
 *                 No persuasion needed, just clear instructions.
 *
 * Avatar:         Driver with tax debt, panicking before May deadline.
 *                 Owes €500–6000 in GPM/VSD/PSD. Needs step-by-step
 *                 instructions to set up installment payments with VMI.
 *
 * Hook:           Empathy + solution — "Negali sumokėti GPM?"
 * Structure:      What is MPS → Eligibility → Step-by-step application → CTA
 * Proof:          VMI's official installment terms, €6000 threshold, 24-month max
 * Funnel:         Rescue content — builds deep trust, converts grateful users
 */
import { motion } from 'framer-motion'
import { PiWarning, PiYoutubeLogo } from 'react-icons/pi'
import { Link } from 'react-router-dom'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const TaxInstallmentPage = () => {
  usePageSetup('GPM mokėti dalimis 2026: mokestinė paskola VMI per 5 minutes', {
    scroll: true,
    description: 'Negalite sumokėti GPM iš karto? VMI leidžia mokėti dalimis iki 24 mėn. be baudų (iki €6 000). Žingsnis po žingsnio — kaip pateikti prašymą per Mano VMI per 5 min.',
    type: 'article',
    datePublished: '2026-03-12',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Negali sumokėti GPM? Kaip mokėti mokesčius dalimis per VMI
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Mokesčiai</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">5 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-3">
          Gegužės 2 d. artėja, o banko sąskaitoje — tuščia. GPM, VSD, PSD — visa tai reikia sumokėti iš karto. Ką daryti, jei pinigų nepakanka?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-8">
          VMI turi oficialią galimybę — <strong>mokestinės paskolos sutartį (MPS)</strong>. Galite mokėti mokesčius dalimis iki 24 mėnesių, o jei skola iki 6 000 € — jokių papildomų dokumentų nereikia.
        </MotionText>

        <div className="border-t border-zinc-200 mt-6 pt-6" />

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium mb-2">
          Kas yra mokestinė paskola?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-sm mb-3">
          Mokestinės paskolos sutartis (MPS) — tai oficiali VMI procedūra, leidžianti mokesčių skolą (GPM, VSD, PSD ar kt.) <strong>išdėstyti lygiomis dalimis</strong> per kelis mėnesius ar net metus.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-sm mb-4">
          Tai ne bauda ir ne nuobauda. Tai VMI siūlomas sprendimas žmonėms, kurie <strong>nori mokėti, bet negali iš karto</strong>.
        </MotionText>

        <MotionDiv className="p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl mb-4">
          <MotionText as="h4" className="text-green-900 text-sm font-medium mb-2">
            Supaprastinta tvarka pavežėjams
          </MotionText>
          <MotionText as="p" className="text-green-800 text-sm mb-2">
            Jei jūsų mokestinė skola <strong>neviršija 6 000 €</strong> ir prašomas laikotarpis <strong>iki 24 mėnesių</strong> — VMI nereikalaus jokių papildomų finansinę būklę įrodančių dokumentų.
          </MotionText>
          <MotionText as="p" className="text-green-700 text-xs">
            Sąlyga: neturite kitų galiojančių MPS ir anksčiau nebuvote pažeidę tokių sutarčių sąlygų.
          </MotionText>
        </MotionDiv>

        <div className="border-t border-zinc-200 mt-6 pt-6" />

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium mb-2">
          Kiek tai kainuoja?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-sm mb-1">
          Už mokestinę paskolą skaičiuojamos palūkanos — <strong>0,007% per dieną nuo likusios skolos sumos</strong>. Kuo greičiau mokate, tuo mažiau palūkanų sumokėsite.
        </MotionText>
        <MotionText as="p" className="text-zinc-400 text-xs mb-3">
          Nuo 2026-02-01, LR finansų ministro įsakymas Nr. 1K-17, pagal MAĮ 88 str.
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="px-4 pt-4 pb-3">
            <MotionText as="h3" className="text-zinc-900 text-base font-medium">
              Pavyzdys: 2 500 € GPM skola per 24 mėn.
            </MotionText>
          </div>

          <div className="px-4 pb-3">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Pagr. įmoka</span>
              <span className="text-zinc-700 font-medium">~104 €/mėn.</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-zinc-500">Palūkanos per 24 mėn.</span>
              <span className="text-zinc-700 font-medium">~67 €</span>
            </div>
            <div className="flex justify-between text-base font-medium mt-2 pt-2 border-t border-zinc-250">
              <span className="text-zinc-900">Iš viso</span>
              <span className="text-zinc-900">~2 567 €</span>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-zinc-200">
            <MotionText as="p" className="text-zinc-500 text-xs font-medium mb-2">
              Palūkanos per dieną (mažėja su likučiu):
            </MotionText>
            <div className="flex gap-2">
              <div className="flex-1 py-2 bg-zinc-50 rounded-xl text-center">
                <p className="text-zinc-500 text-xs">Pradžia</p>
                <p className="text-zinc-900 text-base font-medium">0,18 €</p>
                <p className="text-zinc-400 text-xs">2 500 €</p>
              </div>
              <div className="flex-1 py-2 bg-zinc-50 rounded-xl text-center">
                <p className="text-zinc-500 text-xs">12 mėn.</p>
                <p className="text-zinc-900 text-base font-medium">0,09 €</p>
                <p className="text-zinc-400 text-xs">1 250 €</p>
              </div>
              <div className="flex-1 py-2 bg-zinc-50 rounded-xl text-center">
                <p className="text-zinc-500 text-xs">24 mėn.</p>
                <p className="text-zinc-900 text-base font-medium">0,01 €</p>
                <p className="text-zinc-400 text-xs">104 €</p>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-red-50 border border-red-200 rounded-xl mb-4">
          <MotionText as="p" className="text-red-900 text-sm">
            <strong>Palyginkite:</strong> jei nemokėsite ir nesudarote MPS — delspinigiai + iki 50% bauda + priverstinis išieškojimas iš sąskaitos.
          </MotionText>
        </MotionDiv>

        <div className="border-t border-zinc-200 mt-6 pt-6" />

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium mb-2">
          Kaip pateikti prašymą per Mano VMI
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="relative">
            <div className="absolute left-[2.05rem] top-12 bottom-4 w-px bg-zinc-250" />

            <div className="flex gap-3 px-4 pt-4 pb-3">
              <div className="relative z-10 flex-shrink-0 w-7 h-7 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                1
              </div>
              <div className="pt-0.5">
                <p className="text-zinc-900 text-sm font-medium">Pateikite GPM311 deklaraciją</p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Deklaraciją pateikti privalote iki gegužės 2 d., net jei neturite pinigų mokesčiams. Deklaracijos pateikimas ir mokėjimas — du atskiri veiksmai.
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-4 pb-3">
              <div className="relative z-10 flex-shrink-0 w-7 h-7 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                2
              </div>
              <div className="pt-0.5">
                <p className="text-zinc-900 text-sm font-medium">Prisijunkite prie Mano VMI</p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Eikite į mano.vmi.lt ir prisijunkite per el. bankininkystę arba Smart-ID.
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-4 pb-3">
              <div className="relative z-10 flex-shrink-0 w-7 h-7 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                3
              </div>
              <div className="pt-0.5">
                <p className="text-zinc-900 text-sm font-medium">Užpildykite MPS prašymą</p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Paslaugos → Mokesčių atidėjimas ir termino keitimas → Mokesčių atidėjimo ir mokėjimo dalimis sutartis → Pildyti prašymą
                </p>
              </div>
            </div>

            <div className="flex gap-3 px-4 pb-4">
              <div className="relative z-10 flex-shrink-0 w-7 h-7 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                4
              </div>
              <div className="pt-0.5">
                <p className="text-zinc-900 text-sm font-medium">Nurodykite sumą ir laikotarpį</p>
                <p className="text-zinc-500 text-xs mt-0.5">
                  Pasirinkite, kiek mėnesių norite mokėti (iki 24 mėn. supaprastintai). VMI patvirtins per kelias darbo dienas.
                </p>
              </div>
            </div>
          </div>

          <a
            href="https://www.youtube.com/watch?v=E-43-Tm_5BM"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 border-t border-zinc-200 hover:bg-zinc-150 transition-colors"
          >
            <div className="flex-shrink-0 w-7 h-7 bg-red-600 rounded-lg flex items-center justify-center">
              <PiYoutubeLogo className="w-4 h-4 text-zinc-50" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-zinc-900 text-sm font-medium">VMI video instrukcija</p>
              <p className="text-zinc-500 text-xs">Žingsnis po žingsnio per Mano VMI</p>
            </div>
            <span className="text-zinc-400 text-xs flex-shrink-0">YouTube →</span>
          </a>

          <a
            href="https://www.vmi.lt/evmi/prasymas-isdestyti-atideti-mokesciu-ar-baudu-sumokejima"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 border-t border-zinc-200 hover:bg-zinc-150 transition-colors"
          >
            <div className="flex-shrink-0 w-7 h-7 bg-blue-800 rounded-lg flex items-center justify-center">
              <span className="text-zinc-50 text-xs font-medium">VMI</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-zinc-900 text-sm font-medium">Oficialūs MPS dokumentai</p>
              <p className="text-zinc-500 text-xs">Sąlygos, formos ir teisės aktai</p>
            </div>
            <span className="text-zinc-400 text-xs flex-shrink-0">vmi.lt →</span>
          </a>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="p" className="text-blue-900 text-sm">
            <strong>Kada galima pateikti prašymą?</strong> Nuo balandžio 12 d. (likus 20 dienų iki gegužės 2 d. termino). Galite pateikti ir po termino, kai skola jau susidarė.
          </MotionText>
        </MotionDiv>

        <div className="border-t border-zinc-200 mt-6 pt-6" />

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium mb-2">
          Ko nedaryti
        </MotionText>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                3 didžiausios klaidos
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  ❌ <strong>Nepateikti deklaracijos</strong> — net jei negalite sumokėti, deklaraciją PRIVALOTE pateikti laiku. Kitaip gausite baudą ir už deklaracijos nepateikimą.
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ <strong>Ignoruoti skolą</strong> — VMI pradės priverstinį išieškojimą iš banko sąskaitos. Su MPS to galima išvengti.
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ <strong>Praleisti MPS įmokas</strong> — jei nesumokėsite pagal grafiką, VMI nutrauks sutartį ir pritaikys padidintas palūkanas.
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <div className="border-t border-zinc-200 mt-6 pt-6" />

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium mb-2">
          Dažniausiai užduodami klausimai
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="px-4 pt-4 pb-3 border-b border-zinc-200">
            <p className="text-zinc-900 text-sm font-medium">Ar galiu pateikti prašymą po gegužės 2 d.?</p>
            <p className="text-zinc-500 text-xs mt-1">
              Taip. Galite kreiptis ir tada, kai skola jau susidarė. Tačiau už pavėluotą mokėjimą bus skaičiuojami delspinigiai iki MPS sudarymo momento.
            </p>
          </div>

          <div className="px-4 py-3 border-b border-zinc-200">
            <p className="text-zinc-900 text-sm font-medium">Ar MPS taikoma tik GPM?</p>
            <p className="text-zinc-500 text-xs mt-1">
              Ne. Mokestinės paskolos sutartį galima sudaryti ir dėl VSD, PSD ar kitų mokestinių nepriemokų. Viena sutartis gali apimti kelias skolas.
            </p>
          </div>

          <div className="px-4 py-3 border-b border-zinc-200">
            <p className="text-zinc-900 text-sm font-medium">O jei skola viršija 6 000 €?</p>
            <p className="text-zinc-500 text-xs mt-1">
              Vis tiek galite kreiptis dėl MPS, tačiau VMI gali paprašyti papildomų dokumentų apie jūsų finansinę būklę. Procesas ilgesnis, bet galimybė išlieka.
            </p>
          </div>

          <div className="px-4 py-3">
            <p className="text-zinc-900 text-sm font-medium">Ar VMI nuskaičiuos pinigus iš sąskaitos, jei nesumokėsiu?</p>
            <p className="text-zinc-500 text-xs mt-1">
              Taip — jei nesudarėte MPS ir nesumokėjote laiku, VMI gali pradėti priverstinį išieškojimą. MPS sutartis apsaugo nuo šio veiksmo, kol laikotės mokėjimo grafiko.
            </p>
          </div>
        </MotionDiv>

        <MotionDiv className="p-4 bg-zinc-50 border-l-4 border-zinc-700 rounded-r-xl mb-4">
          <MotionText as="h4" className="text-zinc-900 text-sm font-medium mb-2">
            Kiek tiksliai skolingas? Sužinok iš anksto.
          </MotionText>
          <MotionText as="p" className="text-zinc-700 text-sm mb-2">
            Daugelis pavežėjų apie tikslią mokesčių sumą sužino tik gegužę. Su Finhudas matysite savo GPM, VSD ir PSD <strong>realiu laiku</strong> — ir galėsite planuoti iš anksto, ar reikės MPS.
          </MotionText>
          <Link to="/blog/pavezejo-mokesciai-gpm-psd-vsd" className="text-blue-600 text-sm font-medium underline">
            Kokie mokesčiai laukia 2026 m.? →
          </Link>
        </MotionDiv>

        <FinhudasCTA awareness="problem" />

        <RelatedArticles articles={[
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' },
          { slug: 'mokesciu-kalendorius-kada-moketi-mokescius', title: 'Mokesčių kalendorius 2026: visi terminai' },
          { slug: 'nematomi-mokesciu-spastai-bolt-vairuotojams', title: 'Nematomi mokesčių spąstai: kodėl 80% vairuotojų skolingi VMI' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default TaxInstallmentPage
